import type { UseHeadInput } from '@unhead/vue'
import type { MergeHead } from '@unhead/schema'
import SeoHelper, { type SeoHelperConfig } from '~/utils/SeoHelper'
import { useIsSubdomain } from '~/composables/useSubdomain'

interface UseSeoHelperOptions extends SeoHelperConfig {
  head?: UseHeadInput<MergeHead>
  title?: string
}

const useSeoHelper = (
  options: Ref<UseSeoHelperOptions> | ComputedRef<UseSeoHelperOptions> = ref({})
) => {
  const route = useRoute()
  const { locale } = useI18n()
  const isSubdomain = useIsSubdomain()
  const headers = useRequestHeaders()

  const $i18n = { locale: locale.value }
  const config = useRuntimeConfig()
  const isProduction = config.public.appEnv === 'production'

  const seoHelper = ref<SeoHelper | undefined>(undefined)

  const localeHead = useLocaleHead({
    seo: {
      canonicalQueries: options.value.allowedParams || [],
    },
  })

  const hasCustomHreflang = computed(() => route.meta.hasCustomHreflang)

  watchEffect(() => {
    if (options.value) {
      seoHelper.value = new SeoHelper(
        {
          $i18n,
          $route: route,
          headers,
          isSubdomain: isSubdomain.value,
          isProduction,
          $nuxtI18nHead: localeHead.value,
        },
        {
          ...options.value,
          ...(hasCustomHreflang.value ? { hasCustomHreflang: true } : {}),
        } as SeoHelperConfig
      )
    }
  })

  useHead(
    computed(() => ({
      meta: seoHelper.value?.getHeadMeta(),
      link: seoHelper.value?.getI18nHead().link,
      ...(options.value.head && options.value.head),
    }))
  )
}

export default useSeoHelper
